import { createReducer, createSelector } from 'redux-starter-kit';
import { putPackage, putPackages, putCreditUsages } from './packageActions';
import { userIsAdmin } from '../../../sections/secured/configure/company/user-util';
import { selectSessionUser } from '../../ui/application/sessionUiReducer';
import { selectUserGroup } from '../company/companyReducer';


/**
 * A selector for the outcomes loaded in the domain state.
 * This is a keyed object: { outcomeCode: outcome, ... }
 * @param {object} state The redux state of the application.
 * @returns A keyed object of the inspection outcomes.
 * @example const outcome = selectOutcomesMap(state)[outcomeId];
 */
export const selectPackagesMap = state => state.via.domain.packages;


/**
 * A selector for the inspection outcomes loaded in the domain state.
 * This is a list: [ outcome, outcome, ...]
 * @param {object} state The redux state of the application.
 * @returns An array of inspection outcomes.
 * @example const outcomes = selectOutcomes(state);
 */
export const selectPackages = createSelector(['via.domain.packages'], map => (map ? Object.values(map) : null));

/**
 * A selector for an inspection outcome that is loaded in the domain state.
 * @param {object} state The redux state of the application.
 * @param {number} outcomeCode The id of the outcome.
 * @example const outcome = selectOutcome(state, outcomeCode);
 */
export const selectPackage = (state, code) => {
  const packageMap = selectPackagesMap(state);
  if (!packageMap) return null;

  return packageMap[code];
};

export const purchaseLimitations = {
  emailAddressMissingWithLinks: 'EMAIL_ADDRESS_MISSING_WITH_LINKS',
  emailUnverifiedWithLink: 'EMAIL_UNVERIFIED_WITH_LINK',
  addressMissingWithLink: 'ADDRESS_MISSING_ADMIN_WITH_LINK',
  emailAddressMissingEmailLink: 'EMAIL_ADDRESS_MISSING_EMAIL_LINK',
  addressMissingNoLink: 'ADDRESS_MISSING_NO_LINK',
};

export const selectPurchaseLimitation = (state) => {
  const sessionUser = selectSessionUser(state);
  const userGroup = selectUserGroup(state);
  const companyAddress = userGroup ? userGroup.address : undefined;

  if (userIsAdmin(sessionUser)) {
    if (!sessionUser.emailVerified && companyAddress === null) {
      return purchaseLimitations.emailAddressMissingWithLinks;
    }
    if (!sessionUser.emailVerified) {
      return purchaseLimitations.emailUnverifiedWithLink;
    }
    if (companyAddress === null) {
      return purchaseLimitations.addressMissingWithLink;
    }
  }

  // INSPECTOR and READ-only role
  if (!sessionUser.emailVerified && companyAddress === null) {
    return purchaseLimitations.emailAddressMissingEmailLink;
  }
  if (!sessionUser.emailVerified) {
    return purchaseLimitations.emailUnverifiedWithLink;
  }
  if (companyAddress === null) {
    return purchaseLimitations.addressMissingNoLink;
  }

  return null;
};

export default createReducer({}, {
  // Puts an outcome into the state.
  [putPackage]: (state, action) => {
    const cpackage = action.payload;

    return {
      ...state,
      [cpackage.code]: cpackage,
    };
  },

  // Replaces all the packages in the state.
  [putPackages]: (state, action) => {
    const packages = action.payload;

    return packages.reduce((newState, cpackage) => {
      newState[cpackage.code] = cpackage; // eslint-disable-line no-param-reassign
      return newState;
    }, { });
  },

  [putCreditUsages]: (state, action) => {
    const { packageId, creditUsages } = action.payload;
    return {
      ...state,
      [packageId]: {
        ...state[packageId],
        creditUsages,
      },
    };
  },
});
